import { formatAmountWithUnit } from "@loot-os/api-contract";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { CommandItem } from "@loot-os/ui/components/command";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCurrency } from "../lib/currency";
import type { PickedListing } from "../lib/dropTableDraft";
import { trpcClient } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { PickerCombobox } from "./PickerCombobox";
import { SizeBandBadge } from "./SizeBandBadge";

/**
 * Picks one vendor listing for a drop-table row.
 *
 * Each row reports the value publish will store for the listing, which is above the vendor's
 * asking price by the shipping that price excludes.
 */
export function ListingPicker({
  value,
  sharedOnly,
  taken,
  disabled,
  onPick,
}: {
  /** The row's current listing, or null while it is still empty. */
  value: PickedListing | null;
  /** Box templates may only contain listings every tenant can open. */
  sharedOnly: boolean;
  /** Listings another row of the table already names: a drop table names each once. */
  taken: Set<string>;
  disabled?: boolean;
  onPick: (listing: PickedListing) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const currency = useCurrency();
  const debounced = useDebounced(query);

  const results = useQuery({
    queryKey: [
      sharedOnly ? "platform.boxTemplates.searchListings" : "fulfillment.listings.search",
      debounced,
    ],
    queryFn: () => {
      const input = { ...(debounced ? { query: debounced } : {}), limit: 20 };
      // The box template editor has no tenant, so it asks the platform router for shared rows.
      return sharedOnly
        ? trpcClient.platform.boxTemplates.searchListings.query(input)
        : trpcClient.fulfillment.listings.search.query(input);
    },
    enabled: open,
  });

  return (
    <PickerCombobox
      open={open}
      onOpenChange={setOpen}
      query={query}
      onQueryChange={setQuery}
      triggerLabel={value?.name || "Choose a listing…"}
      searchPlaceholder="Search marketplace listings…"
      emptyMessage={
        results.isFetching ? "Searching…" : (results.error?.message ?? "No matching listings.")
      }
      disabled={disabled}
    >
      {(results.data?.items ?? []).map((listing) => (
        <CommandItem
          key={listing.id}
          value={listing.id}
          onSelect={() => {
            onPick({
              listingId: listing.id,
              name: listing.title,
              imageUrl: listing.imageUrl,
              value: listing.valueCents,
              marketPriceCents: listing.priceCents,
            });
            setOpen(false);
          }}
          disabled={taken.has(listing.id)}
        >
          <BoxImage
            src={listing.imageUrl}
            className="size-8 shrink-0 rounded border object-contain"
          />
          <span className="truncate">{listing.title}</span>
          <SizeBandBadge listingId={listing.id} />
          <span className="ml-auto whitespace-nowrap font-mono text-muted-foreground text-xs">
            {taken.has(listing.id) ? (
              "in the table"
            ) : (
              <>
                {listing.vendor} · {listing.availableQty} left ·{" "}
                {formatAmountWithUnit(listing.valueCents, currency)}
              </>
            )}
          </span>
        </CommandItem>
      ))}
    </PickerCombobox>
  );
}
